import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_CONFIG } from "@/lib/queryConfig";
import type { ProductColor } from "../types";

/**
 * Colours configured for a single catalog product, ordered by display_order.
 *
 * Shared by BOTH the Product Configuration colour manager (admin edits) and
 * the Create-Proof / design-request colour picker (rep picks one). Same cache
 * key per product id so an admin edit reflects in the picker immediately.
 *
 * `subItemId` may be undefined/null (e.g. a proof line not yet bound to a
 * catalog product) — the query is disabled and returns [].
 */
const keyFor = (subItemId: string | null | undefined) =>
  ["product_colors", subItemId ?? "none"] as const;

export function useProductColorsQuery(subItemId: string | null | undefined) {
  return useQuery({
    queryKey: keyFor(subItemId),
    enabled: !!subItemId,
    queryFn: async (): Promise<ProductColor[]> => {
      const { data, error } = await supabase
        .from("product_colors")
        .select("*")
        .eq("sub_item_id", subItemId as string)
        .order("display_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ProductColor[];
    },
    ...QUERY_CONFIG.categories,
  });
}

useProductColorsQuery.keyFor = keyFor;
